import type { ApiContext } from "@/api/types";
import type {
  BatchCosignBody,
  BatchCosignResponse,
  BatchRejectBody,
  BatchRejectResponse,
  CosignatureDocumentType,
  CosignatureQueueResponse,
  CosignatureQueueSearchParams,
  CosignatureStatus,
} from "@/core/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";

type CosignBody = {
  document_type: CosignatureDocumentType;
  patient_id: string;
};

type RejectBody = {
  document_type: CosignatureDocumentType;
  patient_id: string;
  rejection_reason: string;
};

// TODO: move all api response types to core
export type CosignNoteMetaBody = {
  cosignature_date: Nullable<string>;
  cosignature_status: Nullable<CosignatureStatus>;
  rejection_date: Nullable<string>;
  rejection_reason: Nullable<string>;
  reviewing_supervisor_id: Nullable<string>;
  reviewing_supervisor_name: Nullable<string>;
};

export const cosignatureRequestsTyped = {
  batch_cosign: function ({ context, data }: { context?: ApiContext; data: BatchCosignBody }) {
    return httpAuth2
      .post("/v1/cosignature/batch_cosign", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: data,
      })
      .json<BatchCosignResponse>();
  },

  batch_reject: function ({ context, data }: { context?: ApiContext; data: BatchRejectBody }) {
    return httpAuth2
      .post("/v1/cosignature/batch_reject", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: data,
      })
      .json<BatchRejectResponse>();
  },

  cosign_document: function ({
    context,
    data,
    documentId,
  }: {
    context?: ApiContext;
    data: CosignBody;
    documentId: string;
  }) {
    return httpAuth2
      .post(`/v1/cosignature/${documentId}/cosign`, {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: data,
      })
      .json<void>();
  },

  get_cosign_meta: function ({
    context,
    urlParams,
  }: {
    context?: ApiContext;
    urlParams: { noteId: string };
  }) {
    return httpAuth2
      .get(`/v1/cosignature/note/${urlParams.noteId}`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<CosignNoteMetaBody>();
  },

  get_cosign_queue: function ({
    context,
    searchParams,
  }: { context?: ApiContext; searchParams?: CosignatureQueueSearchParams } = {}) {
    return httpAuth2
      .get("/v1/cosignature/queue", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        searchParams,
      })
      .json<CosignatureQueueResponse>();
  },

  get_rejected_notes_count: function ({ context }: { context?: ApiContext } = {}) {
    return httpAuth2
      .get("/v1/cosignature/rejected_count", {
        context: { showLoader: false, ...context },
      })
      .json<{ documents_count: number }>();
  },

  reject_document: function ({
    context,
    data,
    documentId,
  }: {
    context?: ApiContext;
    data: RejectBody;
    documentId: string;
  }) {
    return httpAuth2
      .post(`/v1/cosignature/${documentId}/reject`, {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: data,
      })
      .json<void>();
  },
};
